import { RESET_LINK_TTL_MINUTES } from '@remote-medium/shared';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { AuthLayout } from './AuthLayout.tsx';
import { Button } from './components/button.tsx';
import { Field, FieldLabel } from './components/field.tsx';
import { Input } from './components/input.tsx';
import { Turnstile, useTurnstile } from './Turnstile.tsx';

// The API answers the same way whether or not the address has an account, so
// this page does too: it never confirms that an email exists.
export function ForgotPasswordPage({
  siteUrl,
  accountType,
  turnstileSiteKey,
  requestReset,
}: {
  siteUrl: string;
  /** Names the account in the confirmation copy, e.g. "company" or "curator". */
  accountType: string;
  turnstileSiteKey: string;
  requestReset: (
    email: string,
    turnstileToken: string,
  ) => Promise<{ error?: { message?: string | undefined } | null }>;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const turnstile = useTurnstile();

  async function submit(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const token = await turnstile.getToken();
    if (!token) {
      setError(turnstile.failureMessage());
      setBusy(false);
      return;
    }
    try {
      const { error: err } = await requestReset(email, token);
      if (err) {
        setError(err.message ?? 'Could not send the email. Try again in a moment.');
        turnstile.reset();
      } else {
        setSent(true);
      }
    } catch {
      setError('We could not reach the server. Check your connection and try again.');
      turnstile.reset();
    }
    setBusy(false);
  }

  return (
    <AuthLayout
      siteUrl={siteUrl}
      title="Reset your password"
      intro={
        sent
          ? `If ${email} has a ${accountType} account, a reset link is on its way. It expires in ${RESET_LINK_TTL_MINUTES} minutes.`
          : 'Enter your email and we will send you a link to choose a new password.'
      }
      footer={
        <Link to="/login" className="text-primary underline-offset-4 hover:underline">
          Back to sign in
        </Link>
      }
    >
      {!sent && (
        <form className="grid gap-4" onSubmit={submit}>
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              size="lg"
              id="email"
              type="email"
              autoComplete="username"
              placeholder="you@example.com"
              icon={<Mail />}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Turnstile siteKey={turnstileSiteKey} {...turnstile.widgetProps} />
          {error && (
            <p role="alert" className="m-0 text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" size="lg" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
